/**
 * productionOutputExcel.ts
 *
 * Real Excel (.xlsx) export utility for 7-Stage Production Output Report.
 */
import * as XLSX from 'xlsx'
import type {
  ProductionOutputReportData,
  OutputReportView,
} from '@/services/reports/productionOutputApi'

function getNowTimestampVietnam(): string {
  const now = new Date()
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(now)
}

export function buildProductionOutputWorkbook(
  data: ProductionOutputReportData,
  activeView: OutputReportView = 'by_period',
): XLSX.WorkBook {
  const wb = XLSX.utils.book_new()
  const exportTime = getNowTimestampVietnam()
  const dateRangeStr = `Từ ngày: ${data.filters.from} đến ngày: ${data.filters.to}`

  if (activeView === 'by_period' || activeView === 'order_cumulative') {
    // Sheet 1: Theo kỳ × mã hàng × công đoạn
    const s1Aoa: (string | number)[][] = [
      ['BÁO CÁO SẢN LƯỢNG 7 CÔNG ĐOẠN - THEO KỲ'],
      [`Khoảng ngày: ${dateRangeStr} | Giờ xuất: ${exportTime}`],
      [], // Empty row
      [
        'STT',
        'Kỳ',
        'Mã hàng',
        'Tên hàng',
        'Lệnh SX',
        'Khách hàng',
        // Khối 1: Chi tiết
        'Phôi (chi tiết)',
        'Định hình (chi tiết)',
        // Khối 2: Sản phẩm
        'Lắp ráp (SP)',
        'Chà nhám (SP)',
        'Sơn (SP)',
        'Fitting (SP)',
        'Đóng gói (SP)',
      ],
    ]

    let s1Phoi = 0
    let s1DinhHinh = 0
    let s1LapRap = 0
    let s1ChaNham = 0
    let s1Son = 0
    let s1Fitting = 0
    let s1DongGoi = 0

    data.by_period.forEach((row, idx) => {
      s1Phoi += row.phoi_qty
      s1DinhHinh += row.dinh_hinh_qty
      s1LapRap += row.lap_rap_qty
      s1ChaNham += row.cha_nham_qty
      s1Son += row.son_qty
      s1Fitting += row.fitting_qty
      s1DongGoi += row.dong_goi_qty

      s1Aoa.push([
        idx + 1,
        row.period_label,
        row.product_code,
        row.product_name,
        row.order_no,
        row.customer_name || '—',
        row.phoi_qty,
        row.dinh_hinh_qty,
        row.lap_rap_qty,
        row.cha_nham_qty,
        row.son_qty,
        row.fitting_qty,
        row.dong_goi_qty,
      ])
    })

    // Dòng tổng cộng
    s1Aoa.push([
      'Tổng cộng',
      '',
      '',
      '',
      '',
      '',
      s1Phoi,
      s1DinhHinh,
      s1LapRap,
      s1ChaNham,
      s1Son,
      s1Fitting,
      s1DongGoi,
    ])

    const ws1 = XLSX.utils.aoa_to_sheet(s1Aoa)
    ws1['!cols'] = [
      { wch: 6 },  // STT
      { wch: 24 }, // Kỳ
      { wch: 16 }, // Mã hàng
      { wch: 28 }, // Tên hàng
      { wch: 16 }, // Lệnh SX
      { wch: 20 }, // Khách hàng
      { wch: 16 }, // Phôi
      { wch: 18 }, // Định hình
      { wch: 14 }, // Lắp ráp
      { wch: 14 }, // Chà nhám
      { wch: 12 }, // Sơn
      { wch: 14 }, // Fitting
      { wch: 14 }, // Đóng gói
    ]
    XLSX.utils.book_append_sheet(wb, ws1, 'Theo kỳ và công đoạn')
  }

  if (activeView === 'order_parts' && data.order_parts) {
    // Sheet 2: Chi tiết theo từng chi tiết của lệnh
    const op = data.order_parts
    const s2Aoa: (string | number)[][] = [
      [`BÁO CÁO CHI TIẾT SẢN LƯỢNG MỘC - LỆNH ${op.order_no}`],
      [
        `Mã hàng: ${op.product_code} - ${op.product_name} | KH: ${op.customer_name || '—'} | Kế hoạch: ${op.planned_qty} SP | ${dateRangeStr} | Giờ xuất: ${exportTime}`,
      ],
      [],
      [
        'STT',
        'Tên chi tiết',
        'Quy cách (T×R×D mm)',
        'Loại gỗ',
        'Định mức/SP',
        'Kế hoạch CT (chi tiết)',
        'Phôi trong kỳ (chi tiết)',
        'Định hình trong kỳ (chi tiết)',
        'Tổng kỳ (chi tiết)',
        'Phôi lũy kế (chi tiết)',
        'Định hình lũy kế (chi tiết)',
        'Tổng lũy kế (chi tiết)',
        'Tồn WIP chờ ráp (chi tiết)',
        '% Hoàn thành',
      ],
    ]

    op.parts.forEach((p) => {
      s2Aoa.push([
        p.stt,
        p.part_name,
        p.spec,
        p.wood_type || '—',
        p.qty_per,
        p.target_qty,
        p.phoi_in_period,
        p.dinh_hinh_in_period,
        p.total_in_period,
        p.cum_phoi,
        p.cum_dinh_hinh,
        p.total_cum,
        p.wip_waiting_assembly,
        `${p.progress_pct}%`,
      ])
    })

    // Dòng tổng cộng
    s2Aoa.push([
      'Tổng cộng',
      '',
      '',
      '',
      op.summary.total_qty_per,
      op.summary.total_target_qty,
      op.summary.total_phoi_in_period,
      op.summary.total_dinh_hinh_in_period,
      op.summary.total_phoi_in_period + op.summary.total_dinh_hinh_in_period,
      op.summary.total_cum_phoi,
      op.summary.total_cum_dinh_hinh,
      op.summary.total_cum_phoi + op.summary.total_cum_dinh_hinh,
      op.summary.total_wip_waiting,
      '',
    ])

    const ws2 = XLSX.utils.aoa_to_sheet(s2Aoa)
    ws2['!cols'] = [
      { wch: 6 },  // STT
      { wch: 26 }, // Tên chi tiết
      { wch: 20 }, // Quy cách
      { wch: 14 }, // Loại gỗ
      { wch: 14 }, // Định mức
      { wch: 18 }, // Kế hoạch CT
      { wch: 20 }, // Phôi trong kỳ
      { wch: 24 }, // Định hình trong kỳ
      { wch: 18 }, // Tổng kỳ
      { wch: 20 }, // Phôi lũy kế
      { wch: 24 }, // Định hình lũy kế
      { wch: 20 }, // Tổng lũy kế
      { wch: 22 }, // Tồn WIP
      { wch: 14 }, // %
    ]
    XLSX.utils.book_append_sheet(wb, ws2, 'Chi tiết chi tiết mộc')
  }

  if (activeView === 'order_cumulative') {
    // Sheet 3: Lũy kế theo lệnh
    const s3Aoa: (string | number)[][] = [
      ['BÁO CÁO SẢN LƯỢNG LŨY KẾ THEO LỆNH SẢN XUẤT'],
      [`Khoảng ngày: ${dateRangeStr} | Giờ xuất: ${exportTime}`],
      [],
      [
        'STT',
        'Lệnh SX',
        'Mã hàng',
        'Tên hàng',
        'Khách hàng',
        'Kế hoạch (SP)',
        'Hạn giao khách',
        // Khối 1: Chi tiết
        'Phôi lũy kế (chi tiết)',
        'Định hình lũy kế (chi tiết)',
        // Khối 2: Sản phẩm
        'Lắp ráp (SP)',
        'Chà nhám (SP)',
        'Sơn (SP)',
        'Fitting (SP)',
        'Đóng gói (SP)',
        'Còn lại (SP)',
        '% Hoàn thành',
        'Ngày làm gần nhất',
        'Tình trạng tiến độ',
      ],
    ]

    let s3Planned = 0
    let s3Phoi = 0
    let s3DinhHinh = 0
    let s3LapRap = 0
    let s3ChaNham = 0
    let s3Son = 0
    let s3Fitting = 0
    let s3DongGoi = 0
    let s3Remaining = 0

    data.order_cumulative.forEach((r) => {
      s3Planned += r.planned_qty
      s3Phoi += r.cum_phoi
      s3DinhHinh += r.cum_dinh_hinh
      s3LapRap += r.cum_lap_rap
      s3ChaNham += r.cum_cha_nham
      s3Son += r.cum_son
      s3Fitting += r.cum_fitting
      s3DongGoi += r.cum_dong_goi
      s3Remaining += r.remaining_qty

      s3Aoa.push([
        r.stt,
        r.order_no,
        r.product_code,
        r.product_name,
        r.customer_name || '—',
        r.planned_qty,
        r.requested_ship_date || '—',
        r.cum_phoi,
        r.cum_dinh_hinh,
        r.cum_lap_rap,
        r.cum_cha_nham,
        r.cum_son,
        r.cum_fitting,
        r.cum_dong_goi,
        r.remaining_qty,
        `${r.progress_pct}%`,
        r.latest_event_date || '—',
        r.ship_status.label,
      ])
    })

    // Dòng tổng cộng
    s3Aoa.push([
      'Tổng cộng',
      '',
      '',
      '',
      '',
      s3Planned,
      '',
      s3Phoi,
      s3DinhHinh,
      s3LapRap,
      s3ChaNham,
      s3Son,
      s3Fitting,
      s3DongGoi,
      s3Remaining,
      '',
      '',
      '',
    ])

    const ws3 = XLSX.utils.aoa_to_sheet(s3Aoa)
    ws3['!cols'] = [
      { wch: 6 },  // STT
      { wch: 16 }, // Lệnh SX
      { wch: 16 }, // Mã hàng
      { wch: 28 }, // Tên hàng
      { wch: 20 }, // Khách hàng
      { wch: 14 }, // Kế hoạch SP
      { wch: 16 }, // Hạn giao
      { wch: 20 }, // Phôi
      { wch: 24 }, // Định hình
      { wch: 14 }, // Lắp ráp
      { wch: 14 }, // Chà nhám
      { wch: 12 }, // Sơn
      { wch: 14 }, // Fitting
      { wch: 14 }, // Đóng gói
      { wch: 14 }, // Còn lại
      { wch: 14 }, // %
      { wch: 16 }, // Ngày làm gần nhất
      { wch: 22 }, // Tình trạng
    ]
    XLSX.utils.book_append_sheet(wb, ws3, 'Lũy kế theo lệnh')
  }

  return wb
}

export function exportProductionOutputToExcel(
  data: ProductionOutputReportData,
  activeView: OutputReportView = 'by_period',
): void {
  const wb = buildProductionOutputWorkbook(data, activeView)
  const fromStr = data.filters.from || 'tu-dau'
  const toStr = data.filters.to || 'hien-tai'
  const filename = `SanLuong_${fromStr}_den_${toStr}.xlsx`
  XLSX.writeFile(wb, filename)
}
